// Datas sempre exibidas no fuso de São Paulo, independente do relógio do aparelho.
const TZ = 'America/Sao_Paulo';
const MON = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const parts = new Intl.DateTimeFormat('en-US', { timeZone: TZ, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });

function p(ms: number) {
  const o: Record<string, string> = {};
  for (const x of parts.formatToParts(new Date(ms))) o[x.type] = x.value;
  return { y: o.year, m: o.month, d: o.day, hh: String(+o.hour % 24).padStart(2, '0'), mi: o.minute };
}

export const fmtDate = (ms: number) => { const x = p(ms); return `${x.d}/${x.m}/${x.y}`; };
export const fmtDayMon = (ms: number) => { const x = p(ms); return `${x.d}/${x.m}`; };
export const fmtTime = (ms: number) => { const x = p(ms); return `${x.hh}:${x.mi}`; };
export const dayBox = (ms: number) => { const x = p(ms); return { day: x.d, mon: MON[+x.m - 1] }; };

export function plural(n: number, one: string, many: string) {
  return `${n} ${n === 1 ? one : many}`;
}

export function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase() || '·';
}

export const TINTS = ['#6F8C7C', '#5F7386', '#87715F', '#7C6F8C'];
export function tintFor(key: string) {
  let h = 0;
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) | 0;
  return TINTS[Math.abs(h) % TINTS.length];
}

const SW = ['#D6DED8', '#DAD8D2', '#D4DAE0', '#DCD6DE'];
/** Listras do protótipo: aparecem sob a foto enquanto carrega (ou se faltar). */
export function stripe(id: string) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0;
  return `repeating-linear-gradient(135deg,${SW[Math.abs(h) % 4]} 0 9px,rgba(255,255,255,.55) 9px 18px)`;
}
export const photoBg = (url: string | null | undefined, id: string) =>
  url ? `url("${url}") center / cover no-repeat, ${stripe(id)}` : stripe(id);

export const TIPO_LABEL: Record<string, string> = {
  escola: 'Escola', hospital: 'Hospital', industria: 'Indústria', comercial: 'Restaurante comercial',
};

export function visitBits(v: { fotos: number; novas: number; resolvidas: number; persistem: number }) {
  const bits = [plural(v.fotos, 'foto', 'fotos')];
  if (v.novas) bits.push(plural(v.novas, 'nova', 'novas'));
  if (v.resolvidas) bits.push(plural(v.resolvidas, 'resolvida', 'resolvidas'));
  if (v.persistem) bits.push(`${v.persistem} ${v.persistem === 1 ? 'persiste' : 'persistem'}`);
  return bits.join(' · ');
}

export function formatCnpjInput(v: string) {
  const d = v.replace(/\D/g, '').slice(0, 14);
  return d
    .replace(/^(\d{2})(\d)/, '$1.$2')
    .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
    .replace(/\.(\d{3})(\d)/, '.$1/$2')
    .replace(/(\d{4})(\d)/, '$1-$2');
}

export function formatPhoneInput(v: string) {
  const d = v.replace(/\D/g, '').slice(0, 11);
  if (d.length <= 2) return d ? `(${d}` : '';
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

// ── Nota (mesma regra do Worker: worker/src/lib/nota.ts) ──
export type Classe = 'Bom' | 'Regular' | 'Deficiente';
export const classificar = (n: number): Classe => (n >= 76 ? 'Bom' : n >= 51 ? 'Regular' : 'Deficiente');
export const CLASSE_COR: Record<Classe, { bg: string; fg: string }> = {
  Bom: { bg: '#EDF2EF', fg: '#3F5A4C' },
  Regular: { bg: '#FAF2E6', fg: '#7A5C28' },
  Deficiente: { bg: '#FBEFEC', fg: '#9A4E38' },
};
export const GRAVIDADE_LABEL = { leve: 'Leve', moderada: 'Moderada', grave: 'Grave' } as const;
export const GRAVIDADE_COR = {
  leve: { bg: '#EDF2EF', fg: '#3F5A4C' },
  moderada: { bg: '#FAF2E6', fg: '#7A5C28' },
  grave: { bg: '#FBEFEC', fg: '#9A4E38' },
} as const;
export const NOTA_EXPLICACAO =
  'Cada setor avaliado recebe uma nota de 0 a 100 e a nota da visita é a média dos setores. A nota do restaurante é a média das visitas. Classificação da RDC 275/2002: 76 a 100 Bom, 51 a 75 Regular, 0 a 50 Deficiente.';

export function greeting() {
  const h = +p(Date.now()).hh;
  return h < 12 ? 'Bom dia' : h < 18 ? 'Boa tarde' : 'Boa noite';
}

// ── Dias no formato YYYY-MM-DD (agenda e data da visita), sempre em São Paulo ──
const WD_LONG = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const WD_SHORT = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const MES_LONG = ['janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho', 'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro'];

export const todayIso = () => { const x = p(Date.now()); return `${x.y}-${x.m}-${x.d}`; };
const isoUtc = (iso: string) => { const [y, m, d] = iso.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
export const addDaysIso = (iso: string, n: number) => { const d = isoUtc(iso); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
/** 0 = domingo. */
export const weekdayIso = (iso: string) => isoUtc(iso).getUTCDay();
export const fmtIso = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
export const fmtIsoFull = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
export const fmtIsoShortWd = (iso: string) => WD_SHORT[weekdayIso(iso)];
/** "quinta, 9 de outubro" */
export const fmtIsoLong = (iso: string) => `${WD_LONG[weekdayIso(iso)]}, ${+iso.slice(8, 10)} de ${MES_LONG[+iso.slice(5, 7) - 1]}`;
export const MESES_LONG = MES_LONG;
/** "Hoje", "Amanhã", "Ontem" ou "qui, 09/10". */
export function relDay(iso: string, hoje = todayIso()) {
  if (iso === hoje) return 'Hoje';
  if (iso === addDaysIso(hoje, 1)) return 'Amanhã';
  if (iso === addDaysIso(hoje, -1)) return 'Ontem';
  return `${WD_SHORT[weekdayIso(iso)].toLowerCase()}, ${fmtIso(iso)}`;
}
